import { type RouteObject } from 'react-router-dom';

// ── Pages publiques ───────────────────────────────────────────
import HomePage          from '@/pages/home/page';
import NotFound          from '@/pages/NotFound';
import MobileMoney       from '@/pages/mobile-money/page';
import OfflineFeatures   from '@/pages/offline-features/page';
import GuestCheckout     from '@/pages/guest-checkout/page';
import TrackingPage      from '@/pages/tracking/page';

// ── Auth ──────────────────────────────────────────────────────
import RegisterPage      from '@/pages/auth/register/page';
import LoginPage         from '@/pages/auth/login/page';
import JoinPage          from '@/pages/auth/join/page';

// ── Back-office (nouveau template) ────────────────────────────
import {
  ClientDashboard,
  NewOrder,
  OrdersList,
  OrderDetails,
  ConfirmPayPage,
  LiveTracking,
  NeighborsPage,
  WalletPage,
  AddressesPage,
  ProfilePage,
  SettingsPage,
  NotificationsPage,
  HelpPage,
} from '@/backoffice/pages/client';
import {
  ProDriverDashboard,
  ProMissionsPage,
  ProEarningsPage,
  ProRoutePage,
  ProZonesPage,
  ProDocumentsPage,
  ProInsurancePage,
} from '@/backoffice/pages/pro-driver';
import {
  CommunityDriverDashboard,
  CommunityMissionsPage,
  CommunityTripsPage,
  CommunityEarningsPage,
} from '@/backoffice/pages/community-driver';
import {
  AdminDashboard,
  AdminUsersPage,
  AdminOrdersPage,
  AdminDisputesPage,
  AdminDocumentsPage,
  AdminInfrastructurePage,
  AdminPromotionsPage,
  AdminReportsPage,
  AdminSupportPage,
  AdminCommunicationPage,
} from '@/backoffice/pages/admin';
import {
  SuperAdminDashboard,
  SuperAdminsPage,
  SuperConfigPage,
  SuperAdminReportsPage,
  SuperAdminPricingPage,
  SuperAdminCommunicationPage,
} from '@/backoffice/pages/super-admin';

// ── Dashboard unifié entités (CompanyProfile) ─────────────────
import EntityDashboard from '@/pages/dashboards/entity-dashboard/page';
import { ENTITY_CONFIGS } from '@/domains/company/constants/entity.config';
import CompanyDashboard from '@/pages/auth/company-dashboard/page';

import ProtectedRoute from '@/components/ProtectedRoute';
import PublicRoute from '@/components/PublicRoute';

const routes: RouteObject[] = [

  // ── Pages publiques ────────────────────────────────────────
  { path: '/',                 element: <HomePage /> },
  { path: '/mobile-money',     element: <MobileMoney /> },
  { path: '/offline-features', element: <OfflineFeatures /> },
  { path: '/guest-checkout',   element: <GuestCheckout /> },
  { path: '/tracking',         element: <TrackingPage /> },

  // ── AUTH ───────────────────────────────────────────────────
  {
    element: <PublicRoute />,
    children: [
      { path: '/register/:userType', element: <RegisterPage /> },
      { path: '/login',              element: <LoginPage /> },
      { path: '/join/:token',        element: <JoinPage /> },
    ],
  },

  // ── DASHBOARDS PROTÉGÉS ────────────────────────────────────
  {
    element: <ProtectedRoute />,
    children: [

      // ── Client ────────────────────────────────────────────
      {
        element: <ProtectedRoute allowedRoles={['client']} />,
        children: [
          { path: '/dashboard/client',                      element: <ClientDashboard /> },
          { path: '/dashboard/client/commandes',            element: <OrdersList /> },
          { path: '/dashboard/client/commandes/nouvelle',   element: <NewOrder /> },
          { path: '/dashboard/client/commandes/:id',        element: <OrderDetails /> },
          { path: '/dashboard/client/commandes/:id/paiement', element: <ConfirmPayPage /> },
          { path: '/dashboard/client/suivi',                element: <LiveTracking /> },
          { path: '/dashboard/client/adresses',             element: <AddressesPage /> },
          { path: '/dashboard/client/voisins',              element: <NeighborsPage /> },
          { path: '/dashboard/client/portefeuille',         element: <WalletPage /> },
          { path: '/dashboard/client/historique',           element: <OrdersList /> },
          { path: '/dashboard/client/profil',              element: <ProfilePage /> },
          { path: '/dashboard/client/parametres',          element: <SettingsPage /> },
          { path: '/dashboard/client/notifications',       element: <NotificationsPage /> },
          { path: '/dashboard/client/aide',                element: <HelpPage /> },
        ],
      },

      // ── Livreur communautaire ─────────────────────────────
      {
        element: <ProtectedRoute allowedRoles={['community_delivery']} />,
        children: [
          { path: '/dashboard/livreur',            element: <CommunityDriverDashboard /> },
          { path: '/dashboard/livreur/missions',   element: <CommunityMissionsPage /> },
          { path: '/dashboard/livreur/trajets',    element: <CommunityTripsPage /> },
          { path: '/dashboard/livreur/gains',      element: <CommunityEarningsPage /> },
          { path: '/dashboard/livreur/infos',      element: <CommunityDriverDashboard /> },
        ],
      },

      // ── Livreur professionnel ─────────────────────────────
      {
        element: <ProtectedRoute allowedRoles={['professional_delivery']} />,
        children: [
          { path: '/dashboard/livreur-pro',              element: <ProDriverDashboard /> },
          { path: '/dashboard/livreur-pro/missions',     element: <ProMissionsPage /> },
          { path: '/dashboard/livreur-pro/itineraire',   element: <ProRoutePage /> },
          { path: '/dashboard/livreur-pro/revenus',      element: <ProEarningsPage /> },
          { path: '/dashboard/livreur-pro/zones',        element: <ProZonesPage /> },
          { path: '/dashboard/livreur-pro/documents',    element: <ProDocumentsPage /> },
          { path: '/dashboard/livreur-pro/assurance',    element: <ProInsurancePage /> },
        ],
      },

      // ── Entités company ───────────────────────────────────
      {
        element: <ProtectedRoute allowedRoles={['company']} />,
        children: [
          { path: '/dashboard/company', element: <CompanyDashboard /> },
        ],
      },
      {
        element: <ProtectedRoute allowedRoles={['company']} />,
        children: Object.values(ENTITY_CONFIGS).map((cfg) => ({
          path: cfg.routePath,
          element: <EntityDashboard />,
        })),
      },

      // ── Admin ─────────────────────────────────────────────
      {
        element: <ProtectedRoute allowedRoles={['admin']} />,
        children: [
          { path: '/dashboard/admin',                    element: <AdminDashboard /> },
          { path: '/dashboard/admin/utilisateurs',       element: <AdminUsersPage /> },
          { path: '/dashboard/admin/commandes',          element: <AdminOrdersPage /> },
          { path: '/dashboard/admin/litiges',            element: <AdminDisputesPage /> },
          { path: '/dashboard/admin/documents',          element: <AdminDocumentsPage /> },
          { path: '/dashboard/admin/configuration',    element: <SuperConfigPage /> },
          { path: '/dashboard/admin/infrastructures',    element: <AdminInfrastructurePage /> },
          { path: '/dashboard/admin/promotions',         element: <AdminPromotionsPage /> },
          { path: '/dashboard/admin/support',           element: <AdminSupportPage /> },
          { path: '/dashboard/admin/communication',    element: <AdminCommunicationPage /> },
          { path: '/dashboard/admin/rapports',          element: <AdminReportsPage /> },
        ],
      },

      // ── Super-admin ───────────────────────────────────────
      {
        element: <ProtectedRoute allowedRoles={['super_admin']} />,
        children: [
          { path: '/dashboard/super-admin',                      element: <SuperAdminDashboard /> },
          { path: '/dashboard/super-admin/administrateurs',      element: <SuperAdminsPage /> },
          { path: '/dashboard/super-admin/utilisateurs',         element: <AdminUsersPage /> },
          { path: '/dashboard/super-admin/configuration',        element: <SuperConfigPage /> },
          { path: '/dashboard/super-admin/zones',                element: <AdminInfrastructurePage /> },
          { path: '/dashboard/super-admin/tarification',         element: <SuperAdminPricingPage /> },
          { path: '/dashboard/super-admin/rapports',             element: <SuperAdminReportsPage /> },
          { path: '/dashboard/super-admin/communication',        element: <SuperAdminCommunicationPage /> },
        ],
      },
    ],
  },

  { path: '*', element: <NotFound /> },
];

export default routes;
